import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import { format, startOfDay, endOfDay } from 'date-fns';

/**
 * Modal that displays all shifts and time off for a selected date in a day-view style.
 * Used when clicking a day cell in month view or week view.
 */
function DayShiftsModal({ open, onClose, date, events = [], buildEventTimeLabel }) {
  if (!date) return null;

  const dayStart = startOfDay(new Date(date));
  const dayEnd = endOfDay(new Date(date));

  const dayEvents = events.filter((event) => {
    const eventStart = new Date(event.start);
    const eventEnd = new Date(event.end);
    return eventStart < dayEnd && eventEnd > dayStart;
  });

  const sortedEvents = [...dayEvents].sort(
    (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
  );

  const shifts = sortedEvents.filter((e) => e.extendedProps?.type === 'shift');
  const timeOff = sortedEvents.filter((e) => e.extendedProps?.type === 'timeOff');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="h6" component="span">
          {format(new Date(date), 'EEEE, d MMMM yyyy')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {sortedEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No shifts or time off on this day
          </Typography>
        ) : (
          <List disablePadding>
            {shifts.map((event, idx) => {
              const props = event.extendedProps || {};
              const timeLabel = buildEventTimeLabel ? buildEventTimeLabel(event) : '—';
              return (
                <React.Fragment key={event.id}>
                  {idx > 0 && <Divider component="li" sx={{ my: 1 }} />}
                  <ListItem
                    sx={{
                      px: 0,
                      py: 1.5,
                      alignItems: 'flex-start',
                      borderLeft: '4px solid',
                      borderColor: event.borderColor || 'divider',
                      borderRadius: 1,
                      bgcolor: event.backgroundColor || 'action.hover',
                      mb: 1
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        src={props.teamMemberAvatar}
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: '0.875rem',
                          bgcolor: event.borderColor || 'primary.main'
                        }}
                      >
                        {props.teamMemberInitials || 'ST'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={600}>
                          {props.teamMemberName || event.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {timeLabel}
                        </Typography>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
            {timeOff.map((event, idx) => {
              const props = event.extendedProps || {};
              return (
                <React.Fragment key={event.id}>
                  {(shifts.length > 0 || idx > 0) && <Divider component="li" sx={{ my: 1 }} />}
                  <ListItem
                    sx={{
                      px: 0,
                      py: 1.5,
                      alignItems: 'flex-start',
                      borderLeft: '4px solid',
                      borderColor: event.borderColor || 'divider',
                      borderStyle: 'dashed',
                      borderRadius: 1,
                      bgcolor: event.backgroundColor || 'action.hover',
                      mb: 1
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 44 }}>
                      <Avatar
                        src={props.teamMemberAvatar}
                        sx={{
                          width: 40,
                          height: 40,
                          fontSize: '0.875rem',
                          bgcolor: event.borderColor || 'grey.500'
                        }}
                      >
                        {props.teamMemberInitials || 'ST'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={600}>
                          {props.teamMemberName || event.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          All day
                        </Typography>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              );
            })}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default DayShiftsModal;
